/**
 * NOTE: Depends on entriesRenderer.jsx implementation for specific ads
 * @param {Object} adConfig - obj with data to configure ad
 * @param {Number} index - index of ads of one type
 * @param {Boolean} isMobile
 * @return {Object} - configured to render ad by entriesRenderer.jsx
 */
export const configureAdEntry = (adConfig, index, isMobile) => {
  const { component, id, priorityAtTheBottom, componentConfig = {} } = adConfig;
  if (component) {
    return { id, component, priorityAtTheBottom };
  }

  const { adName, mobileAdName, positions = [], otherAdUnitProps = {} } = componentConfig;
  const updatedAdName = isMobile && mobileAdName ? mobileAdName : adName;
  const position = positions[index];

  return {
    id,
    priorityAtTheBottom,
    options: {
      adName: updatedAdName,
      adNameWired: adName,
      adNameMobile: mobileAdName,
      position,
      ...otherAdUnitProps,
    },
  };
};

/**
 * @param {Number} index - index to insert ad
 * @param {Array} indexesToShift - Array of indexes of already added ad
 * @return {Number} - Updated index if ad was added before it
 */
export const updateAdIndex = (index, indexesToShift = []) => {
  let indexToUpdate = index;
  indexesToShift.forEach(value => {
    if (index >= value) {
      indexToUpdate += 1;
    }
  });

  return indexToUpdate;
};

/**
 * configuration of adsConfig objects:
 * {
 *   @param {String - required} id: Id of ad specified in entries-types.js to render it by entriesRenderer.jsx
 *   @param {Array Of Numbers - required} contentEntriesBeforeAd: Number of content entries after we should place this ad
 *   @param {Number} priorityAtTheBottom: defined ad behavior if content entries less than specified in contentEntriesBeforeAd
 *   0 - will not be added ad the bottom of content. More than 0 - will be added, Larger priority will be higher
 *   @param {Jsx Element} component: If defined Will be inserted as it is
 *   @param {Object} componentConfig: will be used if component not defined. Configuration
 *     {
 *       @param {String - required} adName
 *       @param {String} mobileAdName
 *       @param {Array Of Strings} positions
 *       @param {object} otherAdUnitProps - props to pass to adUnit
 *     }
 * }
 *
 * @param {Array} entries - array of CMS Entries
 * @param {Array} adsConfig - array of obj with ad configuration
 * @param {Boolean} isMobile
 * @return {array} - array of entries with inserted ad
 */
export const insertContentAd = ({ entries, adsConfig = [], isMobile }) => {
  const entriesToUpdate = [...entries];
  const indexesToShift = [];
  const bottomContentAd = [];

  adsConfig.forEach(adConfig => {
    const { priorityAtTheBottom, contentEntriesBeforeAd: indexesToInsert = [] } = adConfig;

    indexesToInsert.forEach((position, index) => {
      const updatedIndex = updateAdIndex(position, indexesToShift);

      if (entriesToUpdate.length > updatedIndex) {
        entriesToUpdate.splice(updatedIndex, 0, configureAdEntry(adConfig, index, isMobile));
        indexesToShift.push(updatedIndex);
      } else if (priorityAtTheBottom > 0) {
        bottomContentAd.push(configureAdEntry(adConfig, index, isMobile));
      }
    });
  });

  return entriesToUpdate.concat(
    bottomContentAd.sort((a, b) => (a.priorityAtTheBottom >= b.priorityAtTheBottom ? -1 : 1))
  );
};
